import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import posthog, { Properties } from 'posthog-js';
import { Profile } from '@trade-platform/ui-shared';
import { LogService } from '@trade-platform/ui-utils';
import { environment } from '../environments/environment';
import { maskReplayText, REPLAY_BLOCK_SELECTOR, scrubCapturedUrls } from './posthog-privacy';

/** The deployment name (`uat`, `production`, `local`, ...); every environment reports to one PostHog project. */
export const posthogEnvironment = environment.environment || environment.id;

/** Database IDs repeat across environments, so the environment prefix keeps UAT and production users apart. */
export function posthogDistinctId(profileId: string): string {
    return `${posthogEnvironment}:${profileId}`;
}

function organizationName(profile: Profile): string | undefined {
    switch (profile.organization?.type) {
        case 'firm':
            return profile.firm?.name;
        case 'fundSponsor':
            return profile.fundSponsor?.name;
        case 'holdingOption':
            return profile.holdingOption?.name;
        default:
            return undefined;
    }
}

/**
 * AIX staff, including the integration and demo accounts. Roles cannot tell staff apart, because
 * `sysAdmin` is also a client-tenant role, so the email domain is the signal.
 */
const INTERNAL_EMAIL_DOMAINS = ['aixplatform.com', 'investwithaqua.com'];

export function isInternalEmail(email: string): boolean {
    const domain = email.trim().toLowerCase().split('@').pop();
    return INTERNAL_EMAIL_DOMAINS.includes(domain ?? '');
}

export function posthogPersonProperties(profile: Profile): Properties {
    return {
        email: profile.email,
        name: profile.fullName,
        roles: profile.roles.map(role => role.name),
        tenant: profile.organization?.tenantIdentifier,
        org_type: profile.organization?.type,
        org_entity_id: profile.organization?.entityId,
        org_name: organizationName(profile),
        is_internal: isInternalEmail(profile.email),
        environment: posthogEnvironment
    };
}

@Injectable({ providedIn: 'root' })
export class PostHogService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly LOG = inject(LogService).getLogger('posthog.service');
    private initialized = false;

    get posthog(): typeof posthog {
        if (isPlatformBrowser(this.platformId) && this.initialized) {
            return posthog;
        }

        return new Proxy({} as typeof posthog, {
            get: () => () => undefined
        });
    }

    get logger(): typeof posthog.logger {
        if (isPlatformBrowser(this.platformId) && this.initialized) {
            return posthog.logger;
        }

        return new Proxy({} as typeof posthog.logger, {
            get: () => () => undefined
        });
    }

    init(): void {
        if (!isPlatformBrowser(this.platformId) || this.initialized) {
            return;
        }

        const { projectToken, host } = environment.posthog;
        if (!this.isConfigured(projectToken) || !this.isConfigured(host)) {
            this.LOG.warn(
                'PostHog is disabled: NG_APP_POSTHOG_PROJECT_TOKEN and NG_APP_POSTHOG_HOST are not configured.'
            );
            return;
        }

        posthog.init(projectToken, {
            api_host: host,
            // Not '2026-06-25' or later: those strip URL hashes, and this app routes by hash.
            defaults: '2026-05-30',
            before_send: scrubCapturedUrls,
            // Autocapture: element text and attributes can hold PII, so neither is sent.
            mask_all_text: true,
            mask_all_element_attributes: true,
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: '*',
                maskTextFn: maskReplayText,
                blockSelector: REPLAY_BLOCK_SELECTOR,
                recordHeaders: false,
                recordBody: false
            },
            enable_recording_console_log: false,
            // Network timing records request URLs, whose query strings can carry search terms.
            capture_performance: { network_timing: false, web_vitals: true },
            logs: {
                serviceName: 'advisor-ui',
                environment: posthogEnvironment,
                serviceVersion: environment.appVersion
            }
        });
        posthog.register({ environment: posthogEnvironment });
        this.initialized = true;
    }

    identify(profile: Profile): void {
        if (!profile.id) {
            return;
        }

        this.posthog.identify(posthogDistinctId(profile.id), posthogPersonProperties(profile));
    }

    /** `reset()` also clears super properties, so the environment is registered again. */
    reset(): void {
        this.posthog.reset();
        this.posthog.register({ environment: posthogEnvironment });
    }

    private isConfigured(value: string): boolean {
        return Boolean(value) && !value.startsWith('{{{');
    }
}
